import React, { SetStateAction, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { formatISO } from 'date-fns';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { StorageLocationAutoComplete } from '@/components/common/storage-location-auto-complete/StorageLocationAutoComplete';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { useUpdateSemen } from '../../../services/semen-pull/hooks';
import { updateSemenSchema } from '../schema';
import { useSemenPullContext } from '../useSemenPullContext';

type Props = {
  updateSemenDialogVisible: boolean;
  setUpdateSemenDialogVisible: React.Dispatch<SetStateAction<boolean>>;
};

export const UpdateSemenDialog = ({
  updateSemenDialogVisible,
  setUpdateSemenDialogVisible,
}: Props) => {
  const { t } = useTranslation();
  const {
    currentScheduledSemen,
    setCurrentScheduledSemen,
    refetchPulledSemen,
    refetchScheduledSemen,
  } = useSemenPullContext();
  const [params] = useSearchParams();
  const selectedSiteId = params.get('site');
  const selectedDate = params.get('date');
  const isSemenPullRestEnabled = useFeatureFlag(
    '29181_semen_pull_graphql_to_rest',
  );

  type UpdateSemenInputs = z.infer<typeof updateSemenSchema>;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<UpdateSemenInputs>({
    resolver: zodResolver(updateSemenSchema),
    defaultValues: {
      semenId: '',
      semenType: '',
      fertSiteId: selectedSiteId || '',
      donorScheduleDate: selectedDate || '',
      sireId: '',
      accountId: '',
      donorScheduleSemenLocation: '',
      donorScheduleSemenType: '',
      cane: '',
      colCode: '',
      naab: '',
      recDate: '0001-01-01',
      quantity: 0,
      move: 0,
      comment: '',
      sexed: 0,
      sex: 0,
      storage: { id: '', name: '' },
      cssCertified: 0,
      secondaryOwner: '',
    },
  });

  const updateSemenMutation = useUpdateSemen();

  useEffect(() => {
    if (!currentScheduledSemen) return;
    setValue('semenId', currentScheduledSemen.semenId as string);
    setValue('semenType', currentScheduledSemen.semenTypeName as string);
    setValue('sireId', currentScheduledSemen.sireId as string);
    setValue('accountId', currentScheduledSemen.accountId as string);
    setValue(
      'donorScheduleSemenLocation',
      currentScheduledSemen.semenStorageId as string,
    );
    setValue(
      'donorScheduleSemenType',
      currentScheduledSemen.semenTypeName as string,
    );
    setValue('cane', currentScheduledSemen.cane as string);
    setValue('colCode', currentScheduledSemen.colCode as string);
    setValue('naab', currentScheduledSemen.naab as string);
    setValue('quantity', currentScheduledSemen.quantity as number);
    setValue('move', currentScheduledSemen.moveNum as number);
    setValue('comment', currentScheduledSemen.comment as string);
    setValue('secondaryOwner', currentScheduledSemen.secondaryOwner ?? '');
  }, [currentScheduledSemen, setValue]);

  const inputs = watch();
  // const updateSemenDisabled =
  //   !(
  //     inputs.storage.id !== '' &&
  //     inputs.cane &&
  //     inputs.colCode &&
  //     inputs.naab
  //   ) || currentScheduledSemen?.quantity === 0;
  const updateSemenDisabled = false;

  const handleUpdateSemen = async (data: UpdateSemenInputs) => {
    const { storage, quantity, ...newSemenInput } = {
      ...data,
      semenId: data.semenId as string,
      semenType: data.semenType as string,
      fertSiteId: data.fertSiteId as string,
      sireId: data.sireId as string,
      donorScheduleDate: formatISO(new Date(selectedDate as string), {
        representation: 'date',
      }),
      accountId: data.accountId as string,
      donorScheduleSemenLocation:
        currentScheduledSemen?.semenStorageId as string,
      donorScheduleSemenType: currentScheduledSemen?.semenTypeName as string,
      cane: data.cane as string,
      colCode: data.colCode as string,
      naab: data.naab as string,
      recDate: data.recDate as string,
      available: data.quantity as number,
      move: data.move as number,
      comment: data.comment as string,
      sexed: data.sexed as number,
      sex: data.sex as number,
      storageId: data.storage.id as string,
      cssCertified: data.cssCertified as number,
      secondaryOwner: data.secondaryOwner ?? null,
    };

    try {
      await updateSemenMutation.mutateAsync(newSemenInput);
      refetchScheduledSemen();
      refetchPulledSemen();
    } catch (error) {
      console.error('Error updating semen:', error);
      throw error;
    }
  };

  return (
    <Dialog
      header={t('common.update') + ' ' + t('semen.semen')}
      visible={updateSemenDialogVisible}
      onHide={() => {
        if (!updateSemenDialogVisible) return;
        setUpdateSemenDialogVisible(false);
        setCurrentScheduledSemen(undefined);
      }}
      style={{ width: '50vw', minWidth: '520px' }}
      maximizable
    >
      <form
        onSubmit={handleSubmit(handleUpdateSemen)}
        className="grid grid-cols-2 gap-x-4 gap-y-4"
      >
        <div className="flex flex-col gap-1">
          <label>Available:</label>
          <Controller
            name="quantity"
            control={control}
            render={({ field }) => <InputNumber disabled {...field} />}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label>Move:</label>
          <Controller
            name="move"
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputNumber
                value={value ?? 0}
                onChange={(e) => onChange(e.value)}
              />
            )}
          />
          {errors.move && (
            <div className="text-sm text-red-500">{errors.move.message}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label>Cane Code:</label>
          <Controller
            name="cane"
            control={control}
            render={({ field }) => <InputText {...field} />}
          />
          {errors.cane && (
            <div className="text-sm text-red-500">{errors.cane.message}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label>Col Code:</label>
          <Controller
            name="colCode"
            control={control}
            render={({ field }) => <InputText {...field} />}
          />
          {errors.colCode && (
            <div className="text-sm text-red-500">{errors.colCode.message}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label>NAAB:</label>
          <Controller
            name="naab"
            control={control}
            render={({ field }) => <InputText {...field} />}
          />
          {errors.naab && (
            <div className="text-sm text-red-500">{errors.naab.message}</div>
          )}
        </div>
        {isSemenPullRestEnabled && (
          <div className="flex flex-col gap-1">
            <label>{t('semen.secondaryOwner')}</label>
            <Controller
              name="secondaryOwner"
              control={control}
              render={({ field }) => (
                <InputText {...field} value={field.value ?? ''} />
              )}
            />
          </div>
        )}
        <div className="col-span-2 flex flex-col gap-1">
          <label>Storage Location:</label>
          <Controller
            name="storage"
            control={control}
            render={({ field }) => (
              <StorageLocationAutoComplete
                label={null}
                value={field.value}
                appendTo={null}
                onSelect={(e) => field.onChange(e.value)}
              />
            )}
          />
          {errors.storage && (
            <div className="text-sm text-red-500">{errors.storage.message}</div>
          )}
        </div>
        <div className="col-span-2 flex justify-end gap-4 pt-2">
          <Button
            label={t('common.update') + ' ' + t('semen.semen')}
            severity="success"
            type="submit"
            loading={updateSemenMutation.isPending}
            disabled={updateSemenDisabled}
          />
          <Button
            label={t('common.cancel')}
            severity="danger"
            onClick={() => {
              setUpdateSemenDialogVisible(false);
            }}
          />
        </div>
      </form>
    </Dialog>
  );
};
